import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = this.closest('product-card');
    const hotspotProduct = this.closest('product-hotspot-component');
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) return url.toString();

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) url.searchParams.set('variant', selectedVariantId);

    return url.toString();
  }

  #getSelectedVariantId() {
    const productCard = this.closest('product-card');
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const variantPicker = modalContent.querySelector('variant-picker');
    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');

        if (gridElement) {
          productGrid = gridElement.cloneNode(true);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      const freshContent = productGrid.cloneNode(true);
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');

    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => {
      this.toggleAttribute('stay-visible', false);
    }, { once: true });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;

    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);

      if (productPrice) productHeader.appendChild(productPrice);

      productGrid.appendChild(productHeader);

      if (variantPicker) productGrid.appendChild(variantPicker);
      if (productFormComponent) productGrid.appendChild(productFormComponent);

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#initTechwearPanel(modalContent);
  }

 #initTechwearPanel(modalContent) {
  if (!modalContent) return;

  const applyQuickAddColorNames = () => {
    const colorLabels = modalContent.querySelectorAll(
      'product-swatches label, .color-swatches-section label'
    );

    colorLabels.forEach((label) => {
      const input =
        label.querySelector('input[type="radio"]') ||
        modalContent.querySelector(
          `#${CSS.escape(label.getAttribute('for') || '')}`
        );

      if (!input || !input.value) return;

      const colorName = input.value.replace(/\s+/g, '\n');

      if (label.getAttribute('data-color-name') !== colorName) {
        label.setAttribute('data-color-name', colorName);
      }
    });
  };

  applyQuickAddColorNames();
  setTimeout(applyQuickAddColorNames, 300);

  const readMoreBtn = modalContent.querySelector('#read-more-toggle');
  const descWrapper = modalContent.querySelector('#desc-wrapper');
  const readMoreArrow = modalContent.querySelector('#read-more-arrow');
  const readMoreLabel = modalContent.querySelector('#read-more-label');

  if (readMoreBtn && descWrapper) {
    readMoreBtn.onclick = () => {
      const isExpanded = descWrapper.classList.toggle('expanded');

      readMoreArrow?.classList.toggle('flipped', isExpanded);

      if (readMoreLabel) {
        readMoreLabel.textContent = isExpanded ? 'Read less' : 'Read more';
      }
    };
  }

  const sizeButtons = modalContent.querySelectorAll('.js-option');

  sizeButtons.forEach((button) => {
    button.onclick = () => {
      sizeButtons.forEach((btn) => btn.classList.remove('active'));
      button.classList.add('active');

      this.#updateTechwearVariant(modalContent);
    };
  });

  const swatchInputs = modalContent.querySelectorAll(
    'product-swatches input, product-swatches label, .color-swatches-section input, .color-swatches-section label'
  );

  swatchInputs.forEach((item) => {
    item.addEventListener('click', () => {
      setTimeout(() => {
        this.#updateTechwearVariant(modalContent);
      }, 80);
    });

    item.addEventListener('change', () => {
      setTimeout(() => {
        this.#updateTechwearVariant(modalContent);
      }, 80);
    });
  });

    const customizeBtn = modalContent.querySelector('#open-customize-modal');
    const customizeModal = modalContent.querySelector('#customize-modal');
    const closeCustomizeBtn = modalContent.querySelector('#close-customize-modal');

    if (customizeBtn && customizeModal) {
      customizeBtn.onclick = () => {
        if (typeof customizeModal.showModal === 'function') {
          customizeModal.showModal();
        } else {
          customizeModal.setAttribute('open', '');
        }
      };
    }

    if (closeCustomizeBtn && customizeModal) {
      closeCustomizeBtn.onclick = () => {
        if (typeof customizeModal.close === 'function') {
          customizeModal.close();
        } else {
          customizeModal.removeAttribute('open');
        }
      };
    }

    const sizeGuideBtn = modalContent.querySelector('#open-size-guide');
    const sizeGuideOverlay = modalContent.querySelector('#size-guide-overlay');
    const closeSizeGuide = modalContent.querySelector('#close-size-guide');

    if (sizeGuideBtn && sizeGuideOverlay) {
      sizeGuideBtn.onclick = () => {
        sizeGuideOverlay.classList.add('open');
      };
    }

    if (closeSizeGuide && sizeGuideOverlay) {
      closeSizeGuide.onclick = () => {
        sizeGuideOverlay.classList.remove('open');
      };
    }

    if (sizeGuideOverlay) {
      sizeGuideOverlay.onclick = (event) => {
        if (event.target === sizeGuideOverlay) {
          sizeGuideOverlay.classList.remove('open');
        }
      };
    }

    const form = modalContent.querySelector('#techwear-form');
    const atcBtn = modalContent.querySelector('#atc-btn');

    if (form && atcBtn) {
      atcBtn.onclick = () => {
        this.#updateTechwearVariant(modalContent);
      };
    }

    this.#updateTechwearVariant(modalContent);
    applyQuickAddColorNames();
  }

  #updateTechwearVariant(modalContent) {
    const jsonEl = modalContent.querySelector('[id^="ProductJson-"]');
    const variantInput = modalContent.querySelector('#variant-id');
    const priceEl = modalContent.querySelector('#price');
    const atcBtn = modalContent.querySelector('#atc-btn');

    if (!jsonEl || !variantInput) return;

    let variants = [];

    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    const selectedOptions = [];

    const activeSize = modalContent.querySelector('.js-option.active');
    const checkedInputs = modalContent.querySelectorAll(
      'product-swatches input:checked, .color-swatches-section input:checked'
    );

    checkedInputs.forEach((input) => {
      if (input.value) selectedOptions.push(input.value);
    });

    if (activeSize?.dataset?.value) {
      selectedOptions.push(activeSize.dataset.value);
    }

    const matchedVariant = variants.find((variant) => {
      if (!variant.options) return false;

      return selectedOptions.every((selectedOption) => {
        return variant.options.includes(selectedOption);
      });
    });

    if (!matchedVariant) return;

    variantInput.value = matchedVariant.id;

    if (priceEl && matchedVariant.price !== undefined) {
      priceEl.textContent = `${this.#formatMoneyWithoutCurrency(matchedVariant.price)} kr`;
    }

    if (atcBtn) {
      atcBtn.disabled = !matchedVariant.available;
      atcBtn.textContent = matchedVariant.available ? 'ADD TO CART' : 'SOLD OUT';
    }

    const mediaEl = modalContent.querySelector('.product-information__media');

    if (mediaEl) {
      let badge = mediaEl.querySelector('#product-soldout-badge');

      if (!badge) {
        badge = document.createElement('div');
        badge.id = 'product-soldout-badge';
        badge.className = 'product-media-soldout-label';
        badge.textContent = 'SOLD OUT';
        mediaEl.appendChild(badge);
      }

      badge.style.display = matchedVariant.available ? 'none' : 'block';
    }
  }

  #formatMoneyWithoutCurrency(cents) {
    const amount = Number(cents || 0) / 100;

    return amount
      .toLocaleString('sv-SE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      .replace(/\s/g, '');
  }

  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');

    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.datasetVariantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }

      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');

      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}